import { getActiveAuditTrail, type LineItemDiff, type LineItemDiffPath, type RuleRunCriteria } from "@rebuild/shared";

type RuleLabel = "category" | "carrier" | "tpa" | "organization" | "miscellaneous";

const CRITERIA_TYPE_TO_LABEL: Record<keyof RuleRunCriteria, RuleLabel | undefined> = {
  catNumbers: "category",
  carriers: "carrier",
  tpas: "tpa",
  organizations: "organization",
  estimateType: undefined,
  isMitToRecon: undefined,
};

const LABEL_ORDER: RuleLabel[] = ["category", "carrier", "tpa", "organization", "miscellaneous"];

/**
 * Look through the active (non-reverted) audit trail of a line item and aggregates the "rule criteria types" into a map containing
 * how many of each rule criteria type was applied.
 *
 * Optional fields:
 * - `pathsToLookAt`: If set, then only return the diffs that were applied to the paths specified in this list
 */
export const getRuleLabelCountsFromAuditTrail = (
  auditTrail: LineItemDiff[],
  opts?: { pathsToLookAt?: LineItemDiffPath[] },
): Partial<Record<RuleLabel, number>> => {
  const ruleIdToLabel = new Map<string, RuleLabel>();
  for (const diff of getActiveAuditTrail(auditTrail)) {
    if (diff.source !== "rules_engine") continue;
    if (ruleIdToLabel.has(diff.ruleUuid)) continue;
    if (opts?.pathsToLookAt !== undefined && !opts.pathsToLookAt.includes(diff.diff.path)) continue;

    const label = diff.ruleCriteriaType ? CRITERIA_TYPE_TO_LABEL[diff.ruleCriteriaType] : undefined;
    ruleIdToLabel.set(diff.ruleUuid, label ?? "miscellaneous");
  }

  const counts: Partial<Record<RuleLabel, number>> = {};
  for (const label of ruleIdToLabel.values()) {
    counts[label] ??= 0;
    counts[label] += 1;
  }
  return counts;
};

/**
 * Formats a rule-count map into a human-readable sentence, ordered by LABEL_ORDER and joined with commas plus an
 * Oxford "and". Singular/plural "rule"/"rules" is chosen per count. Returns "" if no counts are present.
 *
 * Example:
 *   formatRuleCounts("Adjusted by", { category: 2, carrier: 1, miscellaneous: 3 })
 *   // => "Adjusted by 2 category rules, 1 carrier rule, and 3 miscellaneous rules"
 */
export const formatRuleCounts = (prefix: string, counts: Partial<Record<RuleLabel, number>>): string => {
  const parts = LABEL_ORDER.flatMap((label) => {
    const count = counts[label];
    if (count === undefined) return [];
    return [`${count} ${label} ${count === 1 ? "rule" : "rules"}`];
  });

  if (parts.length === 0) return "";
  if (parts.length === 1) return `${prefix} ${parts[0]}`;
  if (parts.length === 2) return `${prefix} ${parts[0]} and ${parts[1]}`;
  return `${prefix} ${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
};
